import React, { useState, useEffect, useContext } from "react";
import { Sidebar, Footer, LoadingIcon } from "../../components";
import { UserContext } from "../../context/UserContext";
import { getReportsSupabase } from "../../consts/supabaseClient";
import { Tabs, Empty, Spin, Alert, Breadcrumb, Tag } from "antd";
import { getOrgs } from "../../utils/api";
import { useLocalStorage } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import { BankOutlined as OrganizationIcon } from "@ant-design/icons";

const Reports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { userData, isAdmin } = useContext(UserContext);

  const [orgData, setOrgData] = useState([]);
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage("dropdowns", null);
  const [settingDefaults, setSettingDefaults] = useState({ org: false });
  const [currentOrg, setCurrentOrg] = useState(null);

  // Reports data
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingIframes, setLoadingIframes] = useState({});

  // Fetch orgs for admin users
  useEffect(() => {
    const fetchOrgs = async () => {
      if (!userData) return;

      if (userData.is_superuser || isAdmin()) {
        try {
          const res = await getOrgs();
          const activeOrgs = res.data.data
            .filter((org) => org.is_active)
            .map((org) => ({
              name: org.name,
              id: org.id,
            }))
            .sort((a, b) =>
              (a?.name || "").localeCompare(b?.name || "", undefined, {
                sensitivity: "base",
              })
            );
          setOrgData(activeOrgs);
        } catch (err) {
          console.error("Error fetching orgs:", err);
        }
      } else {
        // Non-admin: use their organization
        setCurrentOrg(userData.organization_id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      }
    };

    fetchOrgs();
  }, [userData, isAdmin]);

  // Initialize org from URL params or localStorage
  useEffect(() => {
    if (orgData && orgData.length && !settingDefaults.org) {
      const orgFromUrl = searchParams.get("org");
      if (orgFromUrl) {
        const match = orgData.find((o) => o.id === orgFromUrl);
        setCurrentOrg(match ? match.id : defaultDropdown?.org || orgData[0]?.id);
      } else {
        setCurrentOrg(defaultDropdown?.org || orgData[0]?.id);
      }
      setSettingDefaults((prev) => ({ ...prev, org: true }));
    }
  }, [orgData, defaultDropdown, searchParams, settingDefaults.org]);

  // Sync org to URL and localStorage
  useEffect(() => {
    if (currentOrg) {
      if (searchParams.get("org") !== currentOrg) {
        const params = new URLSearchParams(searchParams);
        params.set("org", currentOrg);
        setSearchParams(params);
      }
      setDefaultDropdown((prev) => ({ ...prev, org: currentOrg }));
    }
  }, [currentOrg, searchParams, setSearchParams, setDefaultDropdown]);


  // Fetch reports when org is selected
  useEffect(() => {
    const fetchReports = async () => {
      if (!currentOrg) return;

      try {
        setReportsLoading(true);
        setError(null);

        const supabase = getReportsSupabase();

        const { data, error: fetchError } = await supabase
          .from("reports")
          .select("id, title, url, sort_order, is_active")
          .eq("org_id", currentOrg)
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (fetchError) {
          setError(fetchError.message);
          setReports([]);
        } else {
          setReports(data || []);
        }
      } catch (err) {
        setError(err.message);
        setReports([]);
      } finally {
        setReportsLoading(false);
      }
    };

    fetchReports();
  }, [currentOrg]);

  const isAdminOrSuperuser = userData?.is_superuser || isAdmin();
  const currentOrgName = isAdminOrSuperuser
    ? orgData.find((org) => org.id === currentOrg)?.name
    : userData?.organization?.name;

  const breadcrumbItems = [
    ...(isAdminOrSuperuser ? [{
      title: (
        <Tag color="gold">
          <OrganizationIcon /> {currentOrgName}
        </Tag>
      ),
      menu: {
        items: orgData?.map((org) => ({
          key: org.id,
          label: org.name,
        })),
        onClick: (e) => {
          setCurrentOrg(e.key);
          setSettingDefaults({ org: true });
          const params = new URLSearchParams(searchParams);
          params.set("org", e.key);
          setSearchParams(params);
        },
      },
    }] : []),
  ];

  // Initialize loading states for all reports
  useEffect(() => {
    if (reports.length > 0) {
      const newLoadingStates = {};
      reports.forEach((report) => {
        newLoadingStates[report.id.toString()] = true;
      });
      setLoadingIframes(newLoadingStates);

      // Auto-hide loaders after 3 seconds as fallback for cross-origin iframes
      const timer = setTimeout(() => {
        setLoadingIframes((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            updated[key] = false;
          });
          return updated;
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [reports]);

  const handleIframeLoad = (reportId) => {
    setLoadingIframes((prev) => ({
      ...prev,
      [reportId]: false,
    }));
  };

  const tabItems = reports.map((report) => {
    const reportId = report.id.toString();
    const isLoading = loadingIframes[reportId] === true;

    return {
      key: reportId,
      label: report.title,
      children: isLoading ? (
        <LoadingIcon height="200px" classname="mt-6" />
      ) : (
        <iframe
          src={report.url}
          className="w-[95vw] h-[85vh] border-0 rounded-lg"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation-by-user-activation"
          title={report.title}
          onLoad={() => handleIframeLoad(reportId)}
        />
      ),
    };
  });

  return (
    <div className="flex">
      <Sidebar active="reports" open={sidebarOpen} setOpen={setSidebarOpen} />
      <div
        className={`flex flex-col w-full px-20 lg:px-6 ${
          sidebarOpen ? "pl-60 sm:pl-10" : "sm:pl-14"
        } pt-10 md:pt-4 pb-14 md:pl-15 md:pr-2 sm:pr-4 transition-padding ease-in-out duration-200`}
      >
        <Breadcrumb items={breadcrumbItems} />

        {error && <Alert type="error" message="Error" description={error} showIcon className="mt-2 mb-2" />}

        {reportsLoading ? (
          <Spin size="large" className="mt-10" />
        ) : reports.length === 0 ? (
          <Empty description="No reports available" className="mt-10" />
        ) : (
          <div className="mt-2 [&_.ant-tabs]:border-0 [&_.ant-tabs-nav]:border-0 [&_.ant-tabs-content-holder]:border-0 [&_.ant-tabs-content]:border-0">
            <Tabs items={tabItems} />
          </div>
        )}

        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </div>
  );
};

export default Reports;
